import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, MessageCircle, Mail, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/* ✅ STATIC SETTINGS (NO BACKEND) */
const STATIC_SETTINGS = {
  address:
    "18-D, First Floor, near Dainik Bhaskar Office, Gumtala Sub Urban, Defence Colony, Ranjit Avenue, Amritsar, Punjab 143001",
  phoneDisplay: "078377 74865", // show to user
  phoneDigits: "7837774865", // for tel: and whatsapp
  whatsappDigits: "7837774865",
  email: "info@illuminatesalon.com",
  hours: "10:30 AM – 8:00 PM (All Days)",
};

// ✅ A safer Google Maps embed (less likely to break)
const MAPS_EMBED_URL =
  "https://www.google.com/maps?q=Illuminate%20Salon%20Ranjit%20Avenue%20Amritsar&output=embed";

export default function Contact() {
  // ✅ Start with static settings so render never hits null
  const [settings, setSettings] = useState(STATIC_SETTINGS);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);

  // ✅ If you still want an effect, keep it harmless
  useEffect(() => {
    setSettings(STATIC_SETTINGS);
  }, []);

  // ✅ Memoized links to avoid any weird render-loop lint issues
  const telHref = useMemo(() => `tel:+91${settings.phoneDigits}`, [settings.phoneDigits]);
  const mailHref = useMemo(() => `mailto:${settings.email}`, [settings.email]);
  const waBase = useMemo(() => `https://wa.me/91${settings.whatsappDigits}`, [settings.whatsappDigits]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name?.trim() || !formData.phone?.trim() || !formData.message?.trim()) {
      toast.error("Please fill all required fields");
      return;
    }

    // ✅ Prevent double click
    if (submitting) return;
    setSubmitting(true);

    try {
      const whatsappText = [
        "📩 *New Contact Message*",
        "",
        `👤 *Name:* ${formData.name.trim()}`,
        `📞 *Phone:* ${formData.phone.trim()}`,
        `📝 *Message:* ${formData.message.trim()}`,
      ].join("\n");

      const whatsappUrl = `${waBase}?text=${encodeURIComponent(whatsappText)}`;

      // ✅ Open WhatsApp (works in most browsers)
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");

      toast.success("Opening WhatsApp…");

      // ✅ Reset form
      setFormData({ name: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not open WhatsApp. Please try again.");
    } finally {
      // ✅ Always release button
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24" data-testid="contact-page">
      {/* Header */}
      <section className="section-padding py-16 bg-obsidian text-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Get In Touch</p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-4">Contact Us</h1>
          <p className="text-white/70 max-w-xl mx-auto">
            We'd love to hear from you. Reach out to us anytime.
          </p>
        </motion.div>
      </section>

      <section className="section-padding section-gap">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <h2 className="font-heading text-3xl mb-8">Visit Us</h2>

            <div className="space-y-8">
              {/* Address */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-gold" size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Address</h3>
                  <p className="text-neutral-600 leading-relaxed">{settings.address}</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Illuminate%20Salon%20Ranjit%20Avenue%20Amritsar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold text-sm inline-flex items-center gap-1 mt-2 hover:underline"
                  >
                    Get Directions →
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="text-gold" size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Phone</h3>
                  <a href={telHref} className="text-neutral-600 hover:text-gold transition-colors">
                    {settings.phoneDisplay}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="text-gold" size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Working Hours</h3>
                  <p className="text-neutral-600">{settings.hours}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="text-gold" size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Email</h3>
                  <a href={mailHref} className="text-neutral-600 hover:text-gold transition-colors">
                    {settings.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4 mt-10">
              <a href={telHref} className="btn-primary flex items-center gap-2">
                <Phone size={18} /> Call Now
              </a>
              <a
                href={waBase}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Illuminate%20Salon%20Ranjit%20Avenue%20Amritsar"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2"
              >
                <MapPin size={18} /> Get Directions
              </a>
            </div>

            {/* Map */}
            <div className="mt-10 aspect-video w-full rounded-sm overflow-hidden border border-neutral-200">
              <iframe
                src={MAPS_EMBED_URL}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Illuminate Salon Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="font-heading text-3xl mb-8">Send a Message</h2>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Phone *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Your phone"
                  inputMode="tel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message *</label>
                <Textarea
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                  placeholder="How can we help you?"
                />
              </div>

              <Button type="submit" disabled={submitting} className="btn-gold w-full">
                {submitting ? (
                  "Opening WhatsApp..."
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Send via WhatsApp
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
